import React, { useState, useEffect } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { BackButton } from '../components/ui/back-button';
import { Send, MessageSquare, Shield, User, ChevronRight, Search, Users, Megaphone, ArrowLeft, MessageCircle, Trash2, MoreVertical } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI, membersAPI, getUploadUrl } from '../services/api';
import { toast } from 'sonner';

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [members, setMembers] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [mobileView, setMobileView] = useState('list');
  const [activeConversation, setActiveConversation] = useState(null);
  const { user, isAdmin } = useAuth();

  // Check if user can send broadcasts (admin or bureau member)
  const canSendBroadcast = () => {
    return isAdmin() || user?.bureau_role;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [messagesData, membersData] = await Promise.all([
        messagesAPI.getAll(),
        membersAPI.getAll()
      ]);
      setMessages(messagesData);
      setMembers(membersData.filter(m => m.user_id !== user?.user_id));
    } catch (error) {
      toast.error('Erreur lors du chargement');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      let messageType = 'private';
      let recipientId = selectedUser?.user_id || null;

      if (activeConversation === 'broadcast') {
        messageType = 'broadcast';
        recipientId = null;
      } else if (activeConversation === 'group_chat') {
        messageType = 'group_chat';
        recipientId = null;
      }

      await messagesAPI.send(recipientId, newMessage, messageType);
      
      if (messageType === 'broadcast') {
        toast.success('Annonce envoyée à tous les membres');
      } else if (messageType === 'group_chat') {
        toast.success('Message envoyé dans la discussion');
      } else {
        toast.success('Message envoyé');
      }
      
      setNewMessage('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
      console.error(error);
    }
  };

  // Delete a single message
  const handleDeleteMessage = async (messageId) => {
    console.log('handleDeleteMessage called with:', messageId);
    try {
      await messagesAPI.deleteMessage(messageId);
      toast.success('Message supprimé');
      loadData();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Delete entire conversation with a user
  const handleDeleteConversation = async (userId, userName) => {
    console.log('handleDeleteConversation called with:', userId, userName);
    try {
      await messagesAPI.deleteConversation(userId);
      toast.success('Conversation supprimée');
      setActiveConversation(null);
      setSelectedUser(null);
      setMobileView('list');
      loadData();
    } catch (error) {
      console.error('Delete conversation error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Admin: Clear all messages of a type
  const handleClearAllMessages = async (type) => {
    console.log('handleClearAllMessages called with:', type);
    try {
      await messagesAPI.adminClearMessages(type);
      toast.success(type === 'broadcast' ? 'Annonces vidées' : 'Discussions vidées');
      loadData();
    } catch (error) {
      console.error('Clear all error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  // Group messages by conversation type
  const getConversations = () => {
    const conversations = {};
    
    // Broadcasts (Annonces)
    const broadcasts = messages.filter(m => m.is_admin_broadcast || m.message_type === 'broadcast');
    conversations['broadcast'] = {
      type: 'broadcast',
      name: 'Annonces',
      icon: 'megaphone',
      messages: broadcasts,
      lastMessage: broadcasts[0] || null,
      unread: 0
    };
    
    // Group chat (Discussions entre membres)
    const groupChats = messages.filter(m => m.message_type === 'group_chat');
    conversations['group_chat'] = {
      type: 'group_chat',
      name: 'Discussions',
      icon: 'users',
      messages: groupChats,
      lastMessage: groupChats[0] || null,
      unread: 0
    };
    
    // Private messages
    messages
      .filter(m => !m.is_admin_broadcast && m.message_type !== 'broadcast' && m.message_type !== 'group_chat')
      .forEach(msg => {
        const otherUser = msg.from_user?.user_id === user?.user_id 
          ? msg.to_user 
          : msg.from_user;
        
        if (!otherUser) return;
        
        const key = otherUser.user_id;
        if (!conversations[key]) {
          conversations[key] = {
            type: 'private',
            user: otherUser,
            name: otherUser.name,
            icon: 'user',
            messages: [],
            lastMessage: null,
            unread: 0
          };
        }
        conversations[key].messages.push(msg);
        if (!conversations[key].lastMessage || 
            new Date(msg.created_at) > new Date(conversations[key].lastMessage.created_at)) {
          conversations[key].lastMessage = msg;
        }
      });
    
    // Convert to array and sort
    const convArray = Object.entries(conversations).map(([key, value]) => ({ key, ...value }));
    
    // Keep broadcast and group_chat at top, then sort others by last message
    const special = convArray.filter(c => c.type === 'broadcast' || c.type === 'group_chat');
    const others = convArray.filter(c => c.type === 'private').sort((a, b) => {
      if (!a.lastMessage) return 1;
      if (!b.lastMessage) return -1;
      return new Date(b.lastMessage.created_at) - new Date(a.lastMessage.created_at);
    });
    
    return [...special, ...others];
  };

  const conversations = getConversations();

  // Get messages for active conversation
  const getActiveMessages = () => {
    if (!activeConversation) return [];
    
    if (activeConversation === 'broadcast') {
      return messages.filter(m => m.is_admin_broadcast || m.message_type === 'broadcast')
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    
    if (activeConversation === 'group_chat') {
      return messages.filter(m => m.message_type === 'group_chat')
        .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    }
    
    return messages.filter(m => 
      !m.is_admin_broadcast && m.message_type !== 'broadcast' && m.message_type !== 'group_chat' && (
        m.from_user?.user_id === activeConversation ||
        m.to_user?.user_id === activeConversation
      )
    ).sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  };

  const activeMessages = getActiveMessages();
  const activeConvData = conversations.find(c => 
    c.type === 'broadcast' ? activeConversation === 'broadcast' : 
    c.type === 'group_chat' ? activeConversation === 'group_chat' :
    c.user?.user_id === activeConversation
  );

  const filteredMembers = members.filter(m => 
    m.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-purple-400 text-xl">Chargement...</div>
      </div>
    );
  }

  const handleSelectConversation = (conv) => {
    if (conv.type === 'broadcast') {
      setActiveConversation('broadcast');
      setSelectedUser(null);
    } else if (conv.type === 'group_chat') {
      setActiveConversation('group_chat');
      setSelectedUser(null);
    } else {
      setActiveConversation(conv.user?.user_id);
      setSelectedUser(conv.user);
    }
    setMobileView('chat');
  };

  const handleSelectMember = (member) => {
    setSelectedUser(member);
    setActiveConversation(member.user_id);
    setMobileView('chat');
  };

  const handleBackToList = () => {
    setMobileView('list');
  };

  const getConversationIcon = (conv) => {
    if (conv.type === 'broadcast') {
      return <Megaphone className="h-5 w-5 text-yellow-400" />;
    }
    if (conv.type === 'group_chat') {
      return <Users className="h-5 w-5 text-green-400" />;
    }
    return conv.user?.avatar ? (
      <img 
        src={getUploadUrl(conv.user.avatar)} 
        alt={conv.name}
        className="w-10 h-10 rounded-full object-cover"
      />
    ) : (
      <div className="w-10 h-10 rounded-full bg-purple-600/50 flex items-center justify-center">
        <User className="h-5 w-5 text-purple-300" />
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900/20 via-black to-black pt-20 sm:pt-24 px-2 sm:px-4 pb-12">
      <div className="container mx-auto max-w-6xl">
        <BackButton />
        
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-purple-600 mb-2">
            Messagerie
          </h1>
          <p className="text-gray-400 text-sm sm:text-base">Annonces et discussions entre membres</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6 h-[calc(100vh-200px)] sm:h-[600px]">
          {/* Conversations List - Hidden on mobile when viewing chat */}
          <Card className={`${mobileView === 'chat' ? 'hidden lg:block' : 'block'} bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 overflow-hidden`}>
            <div className="p-3 sm:p-4 border-b border-purple-500/30">
              <h2 className="text-base sm:text-lg font-bold text-purple-300 mb-3">Conversations</h2>
              
              {/* Search */}
              <div className="relative mb-4">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                <input
                  type="text"
                  placeholder="Rechercher un membre..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full bg-black/50 border border-purple-500/30 rounded-lg pl-10 pr-4 py-2 text-white text-sm placeholder-gray-500 focus:outline-none focus:border-purple-500"
                />
              </div>
            </div>

            <div className="overflow-y-auto h-[calc(100%-120px)]">
              {/* Annonces et Discussions en premier */}
              {conversations.filter(c => c.type === 'broadcast' || c.type === 'group_chat').map((conv) => (
                <div
                  key={conv.key}
                  onClick={() => handleSelectConversation(conv)}
                  className={`p-3 sm:p-4 border-b border-purple-500/20 cursor-pointer transition-all hover:bg-purple-900/30 ${
                    activeConversation === conv.key ? 'bg-purple-900/40 border-l-4 border-l-purple-500' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      conv.type === 'broadcast' ? 'bg-yellow-600/30' : 'bg-green-600/30'
                    }`}>
                      {getConversationIcon(conv)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between">
                        <span className={`font-semibold text-sm sm:text-base ${
                          conv.type === 'broadcast' ? 'text-yellow-300' : 'text-green-300'
                        }`}>
                          {conv.name}
                        </span>
                        {conv.lastMessage && (
                          <span className="text-xs text-gray-500">
                            {formatDate(conv.lastMessage.created_at)}
                          </span>
                        )}
                      </div>
                      <p className="text-xs sm:text-sm text-gray-400 truncate">
                        {conv.lastMessage?.content || (conv.type === 'broadcast' ? 'Aucune annonce' : 'Aucun message')}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  </div>
                </div>
              ))}

              {/* Séparateur */}
              <div className="px-4 py-2 bg-purple-900/20">
                <span className="text-xs text-gray-500 uppercase tracking-wider">Messages privés</span>
              </div>

              {/* Private conversations */}
              {conversations.filter(c => c.type === 'private').map((conv) => (
                <div
                  key={conv.key}
                  className={`p-3 sm:p-4 border-b border-purple-500/20 transition-all hover:bg-purple-900/30 ${
                    activeConversation === conv.user?.user_id ? 'bg-purple-900/40 border-l-4 border-l-purple-500' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                      onClick={() => handleSelectConversation(conv)}
                    >
                      {getConversationIcon(conv)}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="font-semibold text-white text-sm sm:text-base">{conv.name}</span>
                          {conv.lastMessage && (
                            <span className="text-xs text-gray-500">
                              {formatDate(conv.lastMessage.created_at)}
                            </span>
                          )}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-400 truncate">
                          {conv.lastMessage?.content || 'Aucun message'}
                        </p>
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-500 hover:text-white" data-testid="conv-menu-trigger">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-black border-purple-500/50">
                        <DropdownMenuItem 
                          onSelect={(e) => {
                            e.preventDefault();
                            handleDeleteConversation(conv.user?.user_id, conv.name);
                          }}
                          className="text-red-400 hover:text-red-300 cursor-pointer"
                          data-testid="delete-conversation-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer la conversation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}

              {/* Members to start new conversation */}
              {searchTerm && filteredMembers.length > 0 && (
                <>
                  <div className="px-4 py-2 bg-purple-900/20">
                    <span className="text-xs text-gray-500 uppercase tracking-wider">Nouveau message</span>
                  </div>
                  {filteredMembers.map((member) => (
                    <div
                      key={member.user_id}
                      onClick={() => handleSelectMember(member)}
                      className="p-3 sm:p-4 border-b border-purple-500/20 cursor-pointer transition-all hover:bg-purple-900/30"
                    >
                      <div className="flex items-center gap-3">
                        {member.avatar ? (
                          <img 
                            src={getUploadUrl(member.avatar)} 
                            alt={member.name}
                            className="w-10 h-10 rounded-full object-cover"
                          />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-purple-600/50 flex items-center justify-center">
                            <User className="h-5 w-5 text-purple-300" />
                          </div>
                        )}
                        <span className="font-semibold text-white text-sm sm:text-base">{member.name}</span>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          </Card>

          {/* Chat Area */}
          <Card className={`${mobileView === 'list' ? 'hidden lg:flex' : 'flex'} lg:col-span-2 bg-gradient-to-br from-purple-900/40 to-black border-2 border-purple-500/30 flex-col overflow-hidden`}>
            {activeConversation ? (
              <>
                {/* Chat Header */}
                <div className="p-3 sm:p-4 border-b border-purple-500/30 flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="lg:hidden text-purple-300 hover:text-purple-100"
                    onClick={handleBackToList}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  
                  {activeConversation === 'broadcast' ? (
                    <div className="w-10 h-10 rounded-full bg-yellow-600/30 flex items-center justify-center">
                      <Megaphone className="h-5 w-5 text-yellow-400" />
                    </div>
                  ) : activeConversation === 'group_chat' ? (
                    <div className="w-10 h-10 rounded-full bg-green-600/30 flex items-center justify-center">
                      <Users className="h-5 w-5 text-green-400" />
                    </div>
                  ) : activeConvData?.user?.avatar ? (
                    <img 
                      src={getUploadUrl(activeConvData.user.avatar)} 
                      alt={activeConvData?.name}
                      className="w-10 h-10 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-purple-600/50 flex items-center justify-center">
                      <User className="h-5 w-5 text-purple-300" />
                    </div>
                  )}
                  <div className="flex-1">
                    <h3 className={`font-bold text-sm sm:text-base ${
                      activeConversation === 'broadcast' ? 'text-yellow-300' : 
                      activeConversation === 'group_chat' ? 'text-green-300' : 'text-white'
                    }`}>
                      {activeConversation === 'broadcast' ? 'Annonces' : 
                       activeConversation === 'group_chat' ? 'Discussions entre membres' :
                       activeConvData?.name}
                    </h3>
                    <p className="text-xs text-gray-400">
                      {activeConversation === 'broadcast' ? 'Messages du bureau et des admins' : 
                       activeConversation === 'group_chat' ? 'Discussion ouverte à tous les membres' :
                       'Message privé'}
                    </p>
                  </div>
                  
                  {/* Admin moderation menu for broadcasts and group chats */}
                  {isAdmin() && (activeConversation === 'broadcast' || activeConversation === 'group_chat') && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-white" data-testid="admin-clear-menu-trigger">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-black border-purple-500/50">
                        <DropdownMenuItem 
                          onSelect={(e) => {
                            e.preventDefault();
                            handleClearAllMessages(activeConversation);
                          }}
                          className="text-red-400 hover:text-red-300 cursor-pointer"
                          data-testid="admin-clear-all-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Vider {activeConversation === 'broadcast' ? 'les annonces' : 'les discussions'}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                  
                  {/* Delete conversation button for private messages */}
                  {activeConversation && activeConversation !== 'broadcast' && activeConversation !== 'group_chat' && activeConvData?.user && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-gray-400 hover:text-white" data-testid="private-conv-menu-trigger">
                          <MoreVertical className="h-5 w-5" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent className="bg-black border-purple-500/50">
                        <DropdownMenuItem 
                          onSelect={(e) => {
                            e.preventDefault();
                            handleDeleteConversation(activeConvData.user.user_id, activeConvData.name);
                          }}
                          className="text-red-400 hover:text-red-300 cursor-pointer"
                          data-testid="delete-private-conv-btn"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer la conversation
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4">
                  {activeMessages.length === 0 ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500 text-sm sm:text-base">
                        {activeConversation === 'broadcast' ? 'Aucune annonce pour le moment' : 
                         activeConversation === 'group_chat' ? 'Aucun message dans la discussion' :
                         'Aucun message'}
                      </p>
                    </div>
                  ) : (
                    activeMessages.map((msg) => {
                      const isOwnMessage = msg.from_user?.user_id === user?.user_id;
                      // Admins can delete any message, members can only delete their own
                      const canDelete = isOwnMessage || isAdmin();
                      
                      return (
                        <div
                          key={msg.message_id}
                          className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
                        >
                          <div className={`group relative max-w-[85%] sm:max-w-[70%] ${
                            isOwnMessage 
                              ? 'bg-purple-600/50' 
                              : activeConversation === 'broadcast' 
                                ? 'bg-yellow-600/30'
                                : activeConversation === 'group_chat'
                                  ? 'bg-green-600/30'
                                  : 'bg-gray-800/50'
                          } rounded-lg p-3`}>
                            {(activeConversation === 'broadcast' || activeConversation === 'group_chat') && 
                             !isOwnMessage && (
                              <p className={`text-xs font-semibold mb-1 ${
                                activeConversation === 'broadcast' ? 'text-yellow-300' : 'text-green-300'
                              }`}>
                                {msg.from_user?.name}
                              </p>
                            )}
                            <p className="text-white text-sm sm:text-base whitespace-pre-wrap pr-6">{msg.content}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {formatDate(msg.created_at)}
                            </p>
                            
                            {/* Delete button - visible on hover */}
                            {canDelete && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  console.log('Delete button clicked for message:', msg.message_id);
                                  handleDeleteMessage(msg.message_id);
                                }}
                                className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 p-1.5 bg-red-600/80 hover:bg-red-600 rounded transition-opacity duration-200"
                                title="Supprimer ce message"
                                data-testid={`delete-msg-${msg.message_id}`}
                              >
                                <Trash2 className="h-3 w-3 text-white" />
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>

                {/* Input - Show based on conversation type */}
                {(activeConversation === 'group_chat' || 
                  activeConversation === 'private' ||
                  (activeConversation !== 'broadcast' && activeConversation !== 'group_chat') ||
                  (activeConversation === 'broadcast' && canSendBroadcast())) && (
                  <form onSubmit={handleSendMessage} className="p-3 sm:p-4 border-t border-purple-500/30">
                    {activeConversation === 'broadcast' && (
                      <p className="text-xs text-yellow-400 mb-2 flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        Vous envoyez une annonce à tous les membres
                      </p>
                    )}
                    <div className="flex gap-2">
                      <Textarea
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={
                          activeConversation === 'broadcast' 
                            ? "Écrire une annonce..." 
                            : activeConversation === 'group_chat'
                              ? "Écrire dans la discussion..."
                              : "Écrire un message..."
                        }
                        className="flex-1 bg-black/50 border-purple-500/30 text-white min-h-[40px] max-h-[100px] text-sm sm:text-base resize-none"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendMessage(e);
                          }
                        }}
                      />
                      <Button 
                        type="submit" 
                        className={`${
                          activeConversation === 'broadcast' 
                            ? 'bg-yellow-600 hover:bg-yellow-500' 
                            : activeConversation === 'group_chat'
                              ? 'bg-green-600 hover:bg-green-500'
                              : 'bg-purple-600 hover:bg-purple-500'
                        } px-3 sm:px-4`}
                        disabled={!newMessage.trim()}
                      >
                        <Send className="h-4 w-4" />
                      </Button>
                    </div>
                  </form>
                )}

                {/* Message for non-authorized users in broadcast */}
                {activeConversation === 'broadcast' && !canSendBroadcast() && (
                  <div className="p-3 sm:p-4 border-t border-purple-500/30 bg-black/30">
                    <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                      <Shield className="h-3 w-3" />
                      Seuls les admins et membres du bureau peuvent envoyer des annonces
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center text-gray-500">
                  <MessageCircle className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-4 opacity-50" />
                  <p className="text-sm sm:text-base">Sélectionnez une conversation</p>
                </div>
              </div>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Messages;
